// src/store/slices/authSlice.js
// Authentication state management

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  delegations: [],
  activeDelegationTenantId: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, accessToken, refreshToken } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    updateTokens: (state, action) => {
      const { accessToken, refreshToken } = action.payload;
      if (accessToken) state.accessToken = accessToken;
      if (refreshToken) state.refreshToken = refreshToken;
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.delegations = [];
      state.activeDelegationTenantId = null;
    },
    setDelegations: (state, { payload }) => {
      state.delegations = payload;
    },
    setDelegationContext: (state, { payload: tenantId }) => {
      state.activeDelegationTenantId = tenantId;
    },
    clearDelegationContext: (state) => {
      state.activeDelegationTenantId = null;
    },
  },
});

export const { setCredentials, updateUser, updateTokens, logout, setDelegations, setDelegationContext, clearDelegationContext } =
  authSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;
// User is only authenticated if they have both a user object AND a valid access token
// This prevents edge cases where user/isAuthenticated flag exists but tokens are missing
export const selectIsAuthenticated = (state) =>
  state.auth.isAuthenticated && !!state.auth.user && !!state.auth.accessToken;

// Delegation selectors
export const selectDelegations = (state) => state.auth.delegations || [];
export const selectHasDelegations = (state) => (state.auth.delegations || []).length > 0;
export const selectActiveDelegationTenantId = (state) => state.auth.activeDelegationTenantId;

// Check if user has a specific permission
export const selectHasPermission = (permission) => (state) => {
  const user = state.auth.user;
  if (!user || !user.roles) return false;

  return user.roles.some(
    (role) => role.permissions && role.permissions[permission] === true
  );
};

// Check if user has any of the specified roles
export const selectHasRole =
  (...roleNames) =>
  (state) => {
    const user = state.auth.user;
    if (!user || !user.roles) return false;

    return user.roles.some((role) => roleNames.includes(role.name));
  };

export default authSlice.reducer;
